import React from 'react';
import { Building2, User } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useCustomerType } from '../context/CustomerTypeContext';
import { trackCustomerTypeSelect } from '../utils/siteAnalytics';

interface CustomerTypeModalProps {
  forceOpen?: boolean;
  onClose?: () => void;
}

/** Paths where auto-prompting moms/kundtyp must not block the UI (portal, checkout, ops). */
export function shouldSuppressCustomerTypeAutoModal(pathname: string, search?: string): boolean {
  const path = pathname.split('?')[0] || '/';
  if (path === '/offert/event' || path.startsWith('/offert/event/')) {
    return true;
  }

  if (search) {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    const partner = params.get('partner');
    const utmSource = params.get('utm_source');
    if (
      (partner === 'skaneevent' || utmSource === 'skaneevent') &&
      (path === '/offert' || path.startsWith('/offert/'))
    ) {
      return true;
    }
  }

  const prefixes = [
    '/mina-bokningar',
    '/kassa',
    '/booking',
    '/admin',
    '/leverantor',
    '/dj-portal',
    '/personal',
    '/betalning',
    '/betala-igen',
    '/payment',
  ];
  return prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

const CustomerTypeModal: React.FC<CustomerTypeModalProps> = ({ forceOpen = false, onClose }) => {
  const location = useLocation();
  const { hasSelectedCustomerType, setCustomerType } = useCustomerType();
  // Auto-open only on marketing/catalog surfaces. Portal/checkout must remain usable
  // for first-time email-link visitors without a prior customerType in localStorage.
  const autoOpen =
    !hasSelectedCustomerType &&
    !shouldSuppressCustomerTypeAutoModal(location.pathname, location.search);
  const isOpen = forceOpen || autoOpen;

  if (!isOpen) return null;

  const selectType = (type: 'private' | 'business') => {
    setCustomerType(type);
    trackCustomerTypeSelect(type);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[120] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-slate-900/95 border border-slate-800 rounded-none p-6 shadow-2xl">
        <p className="text-center text-slate-100 text-sm md:text-base leading-relaxed mb-4">
          Välkommen till Sveriges modernaste uthyrningstjänst för ljud och ljus.
          <br />
          <span className="font-semibold">Vem företräder du?</span>
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => selectType('private')}
            className="inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-none transition-colors"
          >
            <User size={18} />
            Privatperson
          </button>
          <button
            onClick={() => selectType('business')}
            className="inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white font-semibold py-3 px-4 rounded-none border border-slate-700 transition-colors"
          >
            <Building2 size={18} />
            Företag
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerTypeModal;

